import {
  AnsweringClient,
  GetManagedInstrumentsSearchParams,
  IInstrument,
} from "../../clients/answeringClient";
import { describeApiError } from "../../utils/apiError";
import { IAssignment } from "../../UserManagement/types";

/**
 * On the groups routes 401 means the access token died and 403 means the caller
 * may not do this — so a refresh is worth trying for the first and pointless for
 * the second. Routes still on the un-migrated admin router answer 403 for both
 * and have to keep treating it as expiry; see instrument-application-service
 * `docs/gotchas/http-status-codes.md`.
 */
const isExpiredToken = (error: any): boolean =>
  error?.response?.status === 401;

const REFUSAL_MESSAGE = "Você não tem permissão para realizar esta operação.";

export interface CreateGroupModalControllerDelegate {
  didGetAssignments: (assignments: IAssignment[]) => void;
  didCreateGroup: (createdId: number) => void;
  groupCreatedWithoutApplications: (createdId: number, reason: string) => void;
  applicationShouldLogout: () => void;
  genericError: (s?: string) => void;
}

export class CreateGroupModalController {
  constructor(
    private delegate: CreateGroupModalControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) { }

  // TODO(#200): this route is on the un-migrated admin router, so 403 still has
  // to be read as expiry here.
  public async getManagedAssignments(
    accessToken: string,
    alreadyRetried = false
  ): Promise<void> {
    try {
      const response = await this.answeringClient.getManagedAssignments(
        accessToken
      );
      this.delegate.didGetAssignments(response.data.data);
    } catch (error: any) {
      if (error?.response?.status === 403 && !alreadyRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) return;
        return this.getManagedAssignments(newTokens.accessToken, true);
      }
      this.delegate.genericError(
        describeApiError(error, "Não foi possível carregar as aplicações.")
      );
    }
  }

  public async createGroup(
    accessToken: string,
    name: string,
    appIds: number[],
    description?: string,
    alreadyRetried = false
  ): Promise<void> {
    let createdGroupId: number;

    try {
      const result = await this.answeringClient.createGroup(
        accessToken,
        name,
        description
      );
      createdGroupId = (result.data as { created: number }).created;
    } catch (error: any) {
      if (isExpiredToken(error) && !alreadyRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) return;
        return this.createGroup(
          newTokens.accessToken,
          name,
          appIds,
          description,
          true
        );
      }
      return this.delegate.genericError("Erro ao criar o grupo!");
    }

    // Retrying from here must never re-enter the step above: the group exists,
    // and a second attempt would create another one.
    return this.addApplications(accessToken, createdGroupId, appIds);
  }

  private async addApplications(
    accessToken: string,
    groupId: number,
    appIds: number[],
    alreadyRetried = false
  ): Promise<void> {
    if (!appIds.length) {
      return this.delegate.didCreateGroup(groupId);
    }

    try {
      await this.answeringClient.addAppsToGroup(accessToken, appIds, groupId);
      this.delegate.didCreateGroup(groupId);
    } catch (error: any) {
      if (isExpiredToken(error) && !alreadyRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) return;
        return this.addApplications(
          newTokens.accessToken,
          groupId,
          appIds,
          true
        );
      }

      // The group exists by now. Naming it keeps the user from creating a second
      // one to recover, and points them at the group rather than at this form.
      const reason =
        error?.response?.status === 403
          ? REFUSAL_MESSAGE
          : "Erro ao adicionar aplicações ao grupo.";
      this.delegate.groupCreatedWithoutApplications(groupId, reason);
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }

  public async getAvailableInstruments(
    token: string,
    params: GetManagedInstrumentsSearchParams
  ): Promise<IInstrument[]> {
    const resp = await this.answeringClient.getManagedInstrumentsSearch(token, params);
    return resp.data.data;
  }
}
